// Main types export file for the HelpDesk system
import { 
  User, UserRole, UserStatus, createUser, validateUser
} from './User.js';
import { 
  Ticket, TicketStatus, TicketPriority, TicketCategory, createTicket, validateTicket
} from './Ticket.js';
import { 
  KnowledgeArticle, ArticleStatus, ArticleCategory, createKnowledgeArticle, validateKnowledgeArticle
} from './KnowledgeArticle.js';

// Re-export all types and functions
export { 
  User, UserRole, UserStatus, createUser, validateUser,
  Ticket, TicketStatus, TicketPriority, TicketCategory, createTicket, validateTicket,
  KnowledgeArticle, ArticleStatus, ArticleCategory, createKnowledgeArticle, validateKnowledgeArticle
};

// Additional data models
export const Department = {
  id: '',
  name: '',
  description: '',
  manager: '',
  members: [],
  createdAt: '',
  updatedAt: ''
};

export const Workflow = {
  id: '',
  name: '',
  description: '',
  status: 'active',
  conditions: [],
  actions: [],
  createdAt: '',
  updatedAt: ''
};

export const SLA = {
  id: '',
  name: '',
  description: '',
  priority: 'medium',
  responseTime: 0, // in minutes
  resolutionTime: 0, // in minutes
  businessHours: true,
  createdAt: '',
  updatedAt: ''
};

export const Notification = {
  id: '',
  type: 'info',
  title: '',
  message: '',
  recipient: '',
  read: false,
  createdAt: '',
  metadata: {}
};

export const AuditLog = {
  id: '',
  action: '',
  entity: '',
  entityId: '',
  userId: '',
  changes: {},
  timestamp: '',
  ipAddress: '',
  userAgent: ''
};

// Common filter interfaces
export const UserFilters = {
  role: '',
  status: '',
  department: '',
  search: '',
  page: 1,
  limit: 20,
  sortBy: 'createdAt',
  sortOrder: 'desc'
};

export const TicketFilters = {
  status: '',
  priority: '',
  category: '',
  assignedTo: '',
  createdBy: '',
  search: '',
  page: 1,
  limit: 20,
  sortBy: 'createdAt',
  sortOrder: 'desc'
};

export const ArticleFilters = {
  status: '',
  category: '',
  author: '',
  search: '',
  page: 1,
  limit: 20,
  sortBy: 'createdAt',
  sortOrder: 'desc'
};

// API Response interfaces
export const ApiResponse = {
  success: true,
  data: null,
  message: '',
  errors: [],
  pagination: {
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  }
};

export const ApiError = {
  success: false,
  message: '',
  errors: [],
  code: '',
  timestamp: ''
};

// Utility functions for common operations
export const createApiResponse = (data, message = '', pagination = null) => {
  return {
    success: true,
    data,
    message,
    errors: [],
    pagination: pagination || {
      page: 1,
      limit: 20,
      total: Array.isArray(data) ? data.length : 1,
      totalPages: 1
    }
  };
};

export const createApiError = (message, errors = [], code = 'UNKNOWN_ERROR') => {
  return {
    success: false,
    message,
    errors,
    code,
    timestamp: new Date().toISOString()
  };
};

export const createPagination = (page, limit, total) => {
  return {
    page: parseInt(page) || 1,
    limit: parseInt(limit) || 20,
    total: parseInt(total) || 0,
    totalPages: Math.ceil(total / limit)
  };
};

// Data validation utilities
export const validateEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export const validatePhone = (phone) => {
  const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
  return phoneRegex.test(phone.replace(/\s/g, ''));
};

export const validateUrl = (url) => {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

export const sanitizeString = (str) => {
  if (typeof str !== 'string') return '';
  return str.trim().replace(/[<>]/g, '');
};

export const generateId = (prefix = 'id') => {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

export const formatDate = (date, format = 'YYYY-MM-DD HH:mm:ss') => {
  if (!date) return '';
  
  const d = new Date(date);
  if (isNaN(d.getTime())) return '';
  
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const hours = String(d.getHours()).padStart(2, '0');
  const minutes = String(d.getMinutes()).padStart(2, '0');
  const seconds = String(d.getSeconds()).padStart(2, '0');
  
  return format
    .replace('YYYY', year)
    .replace('MM', month)
    .replace('DD', day)
    .replace('HH', hours)
    .replace('mm', minutes)
    .replace('ss', seconds);
};

export const parseDate = (dateString) => {
  if (!dateString) return null;
  
  const date = new Date(dateString);
  return isNaN(date.getTime()) ? null : date;
};

export const isDateInRange = (date, startDate, endDate) => {
  const d = new Date(date);
  const start = new Date(startDate);
  const end = new Date(endDate);
  
  return d >= start && d <= end;
};

export const getDateRange = (days = 30) => {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - days);
  
  return {
    start: start.toISOString(),
    end: end.toISOString()
  };
};

export default {
  User,
  Ticket,
  KnowledgeArticle,
  Department,
  Workflow,
  SLA,
  Notification,
  AuditLog,
  UserFilters,
  TicketFilters,
  ArticleFilters,
  ApiResponse,
  ApiError
};
